interface Options<T> {
  render: (content: unknown) => Promise<T>
  commit: (result: T) => Promise<void>
  onError: (error: unknown) => void
}

// One render through DOM commit at a time, with only the newest edit waiting.
// An RSC action can resolve before its async children finish streaming; waiting
// only for the action lets transitions pile up and interrupt each other.
export const createLivePreviewQueue = <T>({ render, commit, onError }: Options<T>) => {
  let queued: { content: unknown } | null = null
  let running = false
  let disposed = false
  let timer: ReturnType<typeof setTimeout> | undefined

  const pump = async () => {
    timer = undefined
    running = true
    try {
      while (queued && !disposed) {
        const { content } = queued
        queued = null
        try {
          const result = await render(content)
          // Keep the preview moving even when edits outpace the server. Only
          // coalesce waiting inputs; discarding completed renders can starve
          // the preview until the editor stops changing fields.
          if (!disposed) {
            await commit(result)
          }
        } catch (error) {
          if (!disposed) {
            onError(error)
          }
        }
      }
    } finally {
      running = false
    }
  }

  return {
    push: (content: unknown) => {
      if (disposed) {
        return
      }
      queued = { content }
      // Fixed coalescing window, not a debounce that repeated events can
      // postpone forever. In-flight edits drain immediately after commit.
      if (!running && !timer) {
        timer = setTimeout(() => void pump(), 50)
      }
    },
    dispose: () => {
      disposed = true
      queued = null
      clearTimeout(timer)
    },
  }
}
